import type { CpmAccountRestorePreview } from '@/generated/account-disqualification';
import { formatCents } from '@/lib/format/currency';

export type { CpmAccountRestorePreview };

const ERRORS_PREFIX = 'admin.campaigns.accountDisqualification.errors';

const KNOWN_CODES = new Set([
  'cpm_account_unidentified',
  'cpm_account_restore_cap_exceeded',
  'cpm_account_restore_budget_insufficient',
  'cpm_account_restore_user_disqualified',
  'cpm_account_not_found',
  'cpm_account_forbidden',
  'cpm_account_reason_required',
  'cpm_account_already_lifted',
  'cpm_account_already_restored',
  'cpm_account_conflict',
  'cpm_account_failed',
  'ranking_finalized',
]);

const AMOUNT_KEYS = ['overCents', 'missingCents'] as const;

type GraphQLErrorLike = { message?: unknown; extensions?: Record<string, unknown> | null };
type ErrorLike = {
  message?: unknown;
  graphQLErrors?: ReadonlyArray<GraphQLErrorLike> | null;
  errors?: ReadonlyArray<GraphQLErrorLike> | null;
};

export interface AccountDisqualificationError {
  key: string;
  values: Record<string, string | number>;
}

/**
 * Maps a backend account-disqualification failure to an i18n key plus the
 * interpolation values it carries (`overCents`, `missingCents`). Reads both
 * Apollo error shapes (`graphQLErrors` and `errors`). Raw text never leaks:
 * anything unrecognized becomes `cpm_account_failed`.
 */
export function mapAccountDisqualificationError(error: unknown): AccountDisqualificationError {
  const fallback = { key: `${ERRORS_PREFIX}.cpm_account_failed`, values: {} };
  if (error == null || typeof error !== 'object') return fallback;
  const err = error as ErrorLike;
  const entries: GraphQLErrorLike[] = [
    ...(err.graphQLErrors ?? []),
    ...(err.errors ?? []),
    { message: err.message },
  ];
  for (const entry of entries) {
    if (typeof entry?.message !== 'string') continue;
    const code = entry.message.trim();
    if (!KNOWN_CODES.has(code)) continue;
    const values: Record<string, string | number> = {};
    for (const key of AMOUNT_KEYS) {
      const amount = entry.extensions?.[key];
      if (typeof amount === 'number' && Number.isSafeInteger(amount)) values[key] = amount;
    }
    return { key: `${ERRORS_PREFIX}.${code}`, values };
  }
  return fallback;
}

export type RestoreBlocker = null | 'budget' | 'userDisqualified' | 'rankingFinalized';

export interface RestoreDialogState {
  canConfirm: boolean;
  showForce: boolean;
  blocker: RestoreBlocker;
  restoreCents: number;
  budgetChargeCents: number;
}

/**
 * What the restore/lift dialog can offer for a preview. Lifting without
 * restoring clips never moves money, so only a finalized ranking blocks it.
 * A restore is judged on the amounts that would actually be charged: with the
 * cap override ("forceCapped") those are the capped ones.
 */
export function resolveRestoreDialogState(
  preview: CpmAccountRestorePreview,
  opts: { restoreClips: boolean; forceCapped: boolean },
): RestoreDialogState {
  if (preview.ranking_finalized) {
    return { canConfirm: false, showForce: false, blocker: 'rankingFinalized', restoreCents: 0, budgetChargeCents: 0 };
  }
  if (!opts.restoreClips) {
    return { canConfirm: true, showForce: false, blocker: null, restoreCents: 0, budgetChargeCents: 0 };
  }
  const forced = opts.forceCapped && preview.cap_exceeded;
  const restoreCents = forced ? preview.capped_amount_cents : preview.full_amount_cents;
  const budgetChargeCents = forced ? preview.capped_budget_charge_cents : preview.budget_charge_cents;
  const missing = forced
    ? Math.max(0, budgetChargeCents - preview.budget_remaining_cents)
    : preview.budget_missing_cents;

  let blocker: RestoreBlocker = null;
  if (preview.user_disqualified) blocker = 'userDisqualified';
  else if (missing > 0) blocker = 'budget';

  return {
    canConfirm: blocker === null && !(preview.cap_exceeded && !opts.forceCapped),
    showForce: preview.cap_exceeded,
    blocker,
    restoreCents,
    budgetChargeCents,
  };
}

/**
 * The error copy interpolates `{{overCents}}` / `{{missingCents}}` as money.
 * Callers pass the mapper's numeric values through this to get BRL/USD text.
 */
export function formatAccountDisqualificationErrorValues(
  values: Record<string, string | number>,
  currency: string,
): Record<string, string | number> {
  const out: Record<string, string | number> = { ...values };
  for (const key of AMOUNT_KEYS) {
    const amount = values[key];
    if (typeof amount === 'number') out[key] = formatCents(amount, currency);
  }
  return out;
}

/**
 * A restore with zero restorable clips would do nothing, so the dialogs show a
 * note instead and never send it. Lifting the ban without restoring stays valid.
 */
export function hasNothingToRestore(preview: CpmAccountRestorePreview, restoreWanted: boolean): boolean {
  return restoreWanted && !preview.ranking_finalized && preview.restorable_count === 0;
}

export interface AccountReleaseAction {
  /** Whether the confirm button may send (the caller also waits for a pending mutation). */
  canConfirm: boolean;
  /** Whether the lift also restores the clips (always true for a restore). */
  sendRestore: boolean;
}

/**
 * What the lift/restore dialog's confirm button does. A plain lift moves no
 * money, so it needs no restore preview: it is allowed while the preview is
 * loading or after it failed (a loaded preview can still say the ranking is
 * finalized). A ticked lift with nothing restorable is a plain lift too, so
 * no restore blocker applies. A restore needs a preview with clips to restore.
 */
export function resolveAccountReleaseAction({
  mode,
  preview,
  restoreTicked,
}: {
  mode: 'lift' | 'restore';
  preview: CpmAccountRestorePreview | null;
  restoreTicked: boolean;
}): AccountReleaseAction {
  if (mode === 'restore') {
    const canConfirm = !!preview && !hasNothingToRestore(preview, true)
      && resolveRestoreDialogState(preview, { restoreClips: true, forceCapped: false }).canConfirm;
    return { canConfirm, sendRestore: true };
  }
  const plainLift = { canConfirm: !preview?.ranking_finalized, sendRestore: false };
  if (!restoreTicked) return plainLift;
  if (!preview) return { canConfirm: false, sendRestore: false };
  if (hasNothingToRestore(preview, true)) return plainLift;
  return {
    canConfirm: resolveRestoreDialogState(preview, { restoreClips: true, forceCapped: false }).canConfirm,
    sendRestore: true,
  };
}
